/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import CompaniesService from 'api/companies/CompaniesService';
import VehiclesService from 'api/vehicles/VehiclesService';
import InstallerAccessesService from 'api/installerAccesses/InstallerAccessesService';
import {
    CreateInstallerAccessGrantModel,
    InstallerAccessGrantDto,
    UpdateInstallerAccessGrantModel,
} from 'api/installerAccesses/models/InstallerAccessGrantDto';
import UsersService from 'api/users/UsersService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './InstallerAccessScreen.module.scss';
import SearchableSelect from 'common/components/searchableSelect/SearchableSelect';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Select, { StylesConfig } from 'react-select';

interface VehicleOption {
    value: string;
    label: string;
    companyName: string;
}

interface VehicleGroup {
    label: string;
    options: VehicleOption[];
}

const InstallerAccessScreen: React.FC = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const isDetails = type === 'details';
    const isNew = !id;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['INSTALLER_ACCESSES_WRITE']);
    const [validated, setValidated] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [installerOptions, setInstallerOptions] = useState<SelectValueLabel[]>([]);
    const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([]);
    const [allVehicleOptions, setAllVehicleOptions] = useState<VehicleOption[]>([]);
    const [formData, setFormData] = useState<{
        id: string;
        installerCompanyId: string;
        startDate: Date | null;
        endDate: Date | null;
        description: string;
        vehicleIds: string[];
    }>({
        id: '',
        installerCompanyId: '',
        startDate: null,
        endDate: null,
        description: '',
        vehicleIds: [],
    });
    const [item, setItem] = useState<InstallerAccessGrantDto | null>(null);

    const selectStyles: StylesConfig<VehicleOption, true> = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            backgroundColor: 'white',
            borderColor: state.isFocused ? '#38bdf8' : '#ced4da',
            borderRadius: '0.375rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(56, 189, 248, 0.3)' : 'none',
            fontSize: '0.875rem',
        }),
        groupHeading: (base) => ({ ...base, fontWeight: 700, color: '#0f172a', textTransform: 'none', fontSize: '0.875rem' }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e0f2fe' }),
        menu: (base) => ({ ...base, zIndex: 50 }),
    }), []);

    const loadOptions = async () => {
        try {
            Loading.show();
            const [installers, vehiclesPage] = await Promise.all([
                CompaniesService.getCompanies('INSTALLER'),
                VehiclesService.getList({ page: 1, itemsPerPage: 10000 } as any),
            ]);
            setInstallerOptions(installers);

            const vehicles = vehiclesPage?.items || [];
            const grouped = new Map<string, VehicleGroup>();
            const flat: VehicleOption[] = [];
            for (const v of vehicles) {
                if (!v.id || !v.companyId) continue;
                const companyName = v.companyName || v.companyId;
                const opt: VehicleOption = {
                    value: v.id,
                    label: `${v.licensePlate}${v.designation ? ' — ' + v.designation : ''}`,
                    companyName,
                };
                flat.push(opt);
                if (!grouped.has(companyName)) {
                    grouped.set(companyName, { label: companyName, options: [] });
                }
                grouped.get(companyName)!.options.push(opt);
            }
            const groups = Array.from(grouped.values()).sort((a, b) => a.label.localeCompare(b.label));
            for (const g of groups) g.options.sort((a, b) => a.label.localeCompare(b.label));
            setVehicleGroups(groups);
            setAllVehicleOptions(flat);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load options', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    const getData = async () => {
        try {
            if (id) {
                Loading.show();
                const result = await InstallerAccessesService.getById(id);
                setItem(result);
                setFormData({
                    id: result.id || '',
                    installerCompanyId: result.installerCompanyId,
                    startDate: new Date(result.startDate),
                    endDate: new Date(result.endDate),
                    description: result.description || '',
                    vehicleIds: (result.vehicles || []).map(v => v.vehicleId),
                });
                Loading.hide();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get installer access', error);
            toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    };

    useEffect(() => {
        loadOptions().catch(console.error);
    }, []);

    useEffect(() => {
        getData().catch(console.error);
    }, [id]);

    const handleChange = (e: any) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) return;
        if (!formData.startDate || !formData.endDate) {
            toast.error(t('installer_accesses.errors.dates_required'));
            return;
        }
        if (formData.endDate <= formData.startDate) {
            toast.error(t('installer_accesses.errors.end_before_start'));
            return;
        }
        if (formData.vehicleIds.length === 0) {
            toast.error(t('installer_accesses.errors.no_vehicles'));
            return;
        }

        try {
            Loading.show();
            if (formData.id) {
                const model: UpdateInstallerAccessGrantModel = {
                    id: formData.id,
                    installerCompanyId: formData.installerCompanyId,
                    startDate: formData.startDate.toISOString(),
                    endDate: formData.endDate.toISOString(),
                    description: formData.description,
                    vehicleIds: formData.vehicleIds,
                };
                await InstallerAccessesService.update(model);
                navigate(`/installer-access/details/${formData.id}`);
            } else {
                const model: CreateInstallerAccessGrantModel = {
                    installerCompanyId: formData.installerCompanyId,
                    startDate: formData.startDate.toISOString(),
                    endDate: formData.endDate.toISOString(),
                    description: formData.description,
                    vehicleIds: formData.vehicleIds,
                };
                const newId = await InstallerAccessesService.create(model);
                navigate(`/installer-access/details/${newId}`);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast.error(error?.response?.data || t('messages.record_save_error'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t save installer access', error);
                toast.error(t('messages.record_save_error'));
            }
            Loading.hide();
        }
    };

    const onRemove = async () => {
        if (!item) return;
        try {
            await InstallerAccessesService.remove(item);
            navigate('/installer-accesses');
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete installer access', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const selectedVehicleOptions = useMemo(
        () => allVehicleOptions.filter(o => formData.vehicleIds.includes(o.value)),
        [allVehicleOptions, formData.vehicleIds]
    );

    const titleKey = isNew ? 'new' : (isDetails ? 'details' : 'edit');

    return (
        <ScreenTitle title={t('installer_accesses.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => navigate('/installer-accesses')}>{t('installer_accesses.title')}</Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`installer_accesses.${titleKey}`)}</Breadcrumb.Item>
            </Breadcrumb>
            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
                <Form onSubmit={onSubmit} noValidate validated={validated}>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="installerCompanyId">
                                <Form.Label>{t('installer_accesses.installer')}{!isDetails ? '*' : ''}</Form.Label>
                                <SearchableSelect
                                    readOnly={isDetails}
                                    name="installerCompanyId"
                                    required
                                    options={installerOptions}
                                    value={formData.installerCompanyId}
                                    placeholder={t('installer_accesses.installer') ?? ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="description">
                                <Form.Label>{t('installer_accesses.description')}</Form.Label>
                                <Form.Control name="description" type="text" value={formData.description} readOnly={isDetails} onChange={handleChange} placeholder={t('installer_accesses.description')} maxLength={250} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="startDate">
                                <Form.Label>{t('installer_accesses.start_date')}{!isDetails ? '*' : ''}</Form.Label>
                                <DateTimePicker
                                    selected={formData.startDate}
                                    onChange={(d) => setFormData({ ...formData, startDate: d })}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    readOnly={isDetails}
                                    className="form-control"
                                    placeholderText={t('installer_accesses.start_date') ?? ''}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="endDate">
                                <Form.Label>{t('installer_accesses.end_date')}{!isDetails ? '*' : ''}</Form.Label>
                                <DateTimePicker
                                    selected={formData.endDate}
                                    onChange={(d) => setFormData({ ...formData, endDate: d })}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    readOnly={isDetails}
                                    minDate={formData.startDate || undefined}
                                    className="form-control"
                                    placeholderText={t('installer_accesses.end_date') ?? ''}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="vehicles">
                                <Form.Label>{t('installer_accesses.vehicles')}{!isDetails ? '*' : ''}</Form.Label>
                                {isDetails
                                    ? (
                                        <div>
                                            {selectedVehicleOptions.length === 0 && <span className="text-muted">—</span>}
                                            {selectedVehicleOptions.map(o => (
                                                <span key={o.value} className="badge bg-light text-dark border me-1 mb-1">
                                                    {o.companyName} · {o.label}
                                                </span>
                                            ))}
                                        </div>
                                    )
                                    : (
                                        <Select<VehicleOption, true>
                                            isMulti
                                            isClearable
                                            options={vehicleGroups as any}
                                            value={selectedVehicleOptions}
                                            onChange={(opts) => {
                                                setFormData({ ...formData, vehicleIds: (opts || []).map(o => o.value) });
                                            }}
                                            styles={selectStyles}
                                            placeholder={t('installer_accesses.vehicles_placeholder') ?? ''}
                                            filterOption={(option, raw) => {
                                                if (!raw) return true;
                                                const o = option.data;
                                                const q = raw.toLowerCase();
                                                return o.label.toLowerCase().includes(q) || o.companyName.toLowerCase().includes(q);
                                            }}
                                        />
                                    )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className={styles.buttonsContainer}>
                        <Button variant="secondary" type="button" className={styles.button} onClick={() => navigate('/installer-accesses')}>
                            {t('common.cancel')}
                        </Button>
                        {isDetails && hasWritePolicy && (
                            <Button variant="danger" type="button" className={styles.button} onClick={() => setShowRemoveModal(true)}>
                                {t('common.delete')}
                            </Button>
                        )}
                        {isDetails && hasWritePolicy && (
                            <Button variant="warning" type="button" className={styles.button} onClick={() => navigate(`/installer-access/edit/${item?.id}`)}>
                                {t('common.edit')}
                            </Button>
                        )}
                        {!isDetails && hasWritePolicy && (
                            <Button variant="primary" type="submit" className={styles.button}>
                                {t('common.save')}
                            </Button>
                        )}
                    </div>
                </Form>
            </div>
            <QuestionYesNo
                onNo={() => setShowRemoveModal(false)}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('messages.remove_record')}
            />
        </ScreenTitle>
    );
};

export default InstallerAccessScreen;
